import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import { IconSearch, IconX } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
//@ts-ignore
import { InputGroup } from 'dsm-react-lib'
import { staticAssetsPath } from '../../../../configs/constants'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { PromptLibraryIcon } from '../icons/Icons'
import { useThemeManager } from '../../../../_hooks/useThemeManager'

interface PromptLibraryModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  suggestions: any
  setPromptText: (description: string) => void
}

const PromptLibraryModal: React.FC<PromptLibraryModalProps> = ({
  open,
  setOpen,
  suggestions,
  setPromptText
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredPrompts, setFilteredPrompts] = useState(suggestions)
  const { appendToken } = useSasToken()
  const { isDarkTheme } = useThemeManager()
  const imgPath = appendToken(`${staticAssetsPath}/images`)

  useEffect(() => {
    if (suggestions) setFilteredPrompts(suggestions)
  }, [])

  useEffect(() => {
    if (searchTerm.length > 0) {
      setFilteredPrompts(
        suggestions?.filter(
          (item: any) => item?.title?.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1
        )
      )
    } else {
      setFilteredPrompts([])
    }
  }, [searchTerm])

  //@ts-ignore
  const trackPromptClick = useTrackEvent(appInsights, EventType.PromptCardClicked)
  const onClose = () => setOpen(!open)

  const handleSearch = (event: any) => {
    setSearchTerm(event.target.value)
  }

  const renderPrompts = (prompts: any) => {
    return prompts?.map((suggestion: any) => (
      <div
        key={suggestion.id}
        className={`min-h-[80px] rounded-md cursor-pointer theme-surface theme-interactive-item border theme-border-default transition-colors duration-200 w-full gap-1 h-full flex items-center justify-center px-2 chat-primary`}
        onClick={() => {
          trackPromptClick({ prompt: suggestion.title })
          setPromptText(suggestion.description)
          onClose()
        }}>
        <div className="flex-shrink-0 flex items-center justify-center w-[40px] h-[40px] rounded-full theme-surface border theme-border-default prompt-icon-container">
          <span className="text-2xl">{suggestion?.icon}</span>
        </div>
        <h4 className="ml-2 flex-1 font-medium line-clamp-2 flex items-center justify-center min-h-[65px] my-auto font-inherit leading-tight theme-text-heading text-lg">
          {suggestion?.title}
        </h4>
      </div>
    ))
  }

  return (
    <Modal
      isOpen={open}
      centered
      contentClassName="theme-surface overflow-hidden rounded-[8px] border theme-border-default"
      className="!max-w-[1000px] !w-full !border-none"
      data-testid="prompt-library-modal">
      <ModalHeader
        className="!justify-between !border-0 !flex !items-center bg-primary !py-3 !px-6"
        data-testid="closeBtn"
        toggle={onClose}
        close={
          <IconX
            size={24}
            onClick={onClose}
            className="text-white opacity-80 transition-opacity duration-300 ease-in-out hover:opacity-100 cursor-pointer"
            data-testid="closeButton"
          />
        }>
        <div className="flex items-center gap-25">
          <PromptLibraryIcon color="var(--white)" size={22} />
          <h3 className="subsection-header text-white m-0">Ali Prompt library</h3>
        </div>
      </ModalHeader>
      <ModalBody className="max-h-[75vh] overflow-y-auto custom-scrollbar pt-2 pb-6 px-6 w-full theme-surface">
        <InputGroup
          addon={<IconSearch size={16} className="theme-input-text" />}
          addonPosition="start"
          addonType="merged"
          isDarkMode={isDarkTheme}
          placeholder="Search prompts"
          onChange={handleSearch}
          id="searchApps"
          data-testid="searchApp"
          className="mb-2 search-input theme-input-group"
        />
        <div className="grid grid-cols-3 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-[15px] w-full prompt-cards">
          {searchTerm === '' ? renderPrompts(suggestions) : renderPrompts(filteredPrompts)}
          {searchTerm !== '' && filteredPrompts.length === 0 && (
            <div className="col-span-3 w-full mt-2 text-center">
              <h4 className="mb-4 p-2 theme-text-subheading w-full">
                No prompts available for the term <b className="theme-text-heading">{searchTerm}</b>
                . Please check your spelling or try a different keyword.
              </h4>
              {imgPath ? (
                <div className="flex justify-center w-full">
                  <img
                    className="max-w-[250px] opacity-80 mx-auto"
                    src={`${imgPath}/ali-noReports.svg`}
                    alt="No results"
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}

export default PromptLibraryModal
